<template>
  <div class="space-y-6">
    <div class="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
      <h3 class="text-lg font-semibold text-slate-800 mb-4">Importación Masiva de Productos</h3>
      
      <div class="space-y-4">
        <div class="mb-4">
          <label class="block text-sm font-medium text-slate-700 mb-2">Categoría</label>
          <select v-model="selectedCategory" class="input-field max-w-xs" required>
            <option value="">Seleccionar categoría</option>
            <option v-for="[id, label] in Object.entries(categoryStore.categories)" :key="id" :value="id">
              {{ label }}
            </option>
          </select>
        </div>

        <p class="text-sm text-slate-600">
          Importe productos masivamente utilizando un archivo Excel. Descargue la plantilla para ver el formato requerido.
        </p>

        <div v-if="selectedCategory && configStore.getCategoryConfig(selectedCategory).hasNotes" 
             class="bg-blue-50 p-4 rounded-lg text-sm text-blue-700">
          <p>
            <strong>Nota:</strong> Esta categoría está configurada para usar notas. 
            El campo "Rango" del Excel se utilizará como campo de notas.
          </p>
        </div>

        <div class="flex flex-wrap gap-4">
          <button 
            @click="downloadTemplate" 
            class="btn-primary bg-white border border-indigo-500 text-indigo-500 hover:bg-indigo-50"
            :disabled="processing"
          >
            Descargar Plantilla Excel
          </button>
          
          <label class="relative cursor-pointer">
            <input
              ref="fileInput"
              type="file"
              accept=".xlsx,.xls"
              class="hidden"
              @change="handleFileUpload"
              :disabled="processing || !selectedCategory"
            />
            <span 
              class="btn-primary"
              :class="{ 'opacity-50 cursor-not-allowed': processing || !selectedCategory }"
            >
              {{ processing ? 'Procesando...' : 'Seleccionar Archivo Excel' }}
            </span>
          </label>
        </div>

        <p v-if="error" class="text-sm text-red-600 mt-2">{{ error }}</p>
      </div>
    </div>

    <!-- Preview Modal -->
    <Modal
      v-if="showPreviewModal"
      :show="showPreviewModal"
      title="Vista Previa de Importación"
      @close="closeModal"
    >
      <div class="space-y-6">
        <!-- Progress bar section -->
        <div v-if="importStore.importing" class="space-y-4">
          <div class="relative pt-1">
            <div class="flex mb-2 items-center justify-between">
              <div>
                <span class="text-xs font-semibold inline-block text-indigo-600">
                  Progreso: {{ importStore.progress }}%
                </span>
              </div>
              <div class="text-right">
                <span class="text-xs font-semibold inline-block text-indigo-600">
                  {{ importStore.currentProduct }}
                </span>
              </div>
            </div>
            <div class="overflow-hidden h-2 mb-4 text-xs flex rounded bg-indigo-200">
              <div
                :style="{ width: `${importStore.progress}%` }"
                class="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-500 transition-all duration-500"
              />
            </div>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-slate-700 mb-2">
            Categoría Seleccionada: {{ categoryStore.categories[selectedCategory] }}
          </label>
        </div>

        <!-- Skipped Products -->
        <div v-if="skippedProducts.length > 0" class="mb-6">
          <h4 class="text-sm font-medium text-yellow-800 mb-2">
            Productos salteados ({{ skippedProducts.length }})
          </h4>
          <div class="bg-yellow-50 rounded-lg p-4">
            <ul class="list-disc list-inside">
              <li 
                v-for="(product, index) in skippedProducts" 
                :key="index"
                class="text-sm text-yellow-700"
              >
                {{ product.name }} - {{ product.reason }}
              </li>
            </ul>
          </div>
        </div>

        <!-- Invalid Rows -->
        <div v-if="Object.keys(invalidRows).length > 0" class="mb-6">
          <h4 class="text-sm font-medium text-red-800 mb-2">
            Filas con errores ({{ Object.keys(invalidRows).length }})
          </h4>
          <div class="bg-red-50 rounded-lg p-4">
            <div v-for="(errors, rowIndex) in invalidRows" :key="rowIndex" class="mb-4">
              <h5 class="text-sm font-medium text-red-700">
                Fila {{ Number(rowIndex) + 1 }}:
              </h5>
              <ul class="mt-1 list-disc list-inside">
                <li 
                  v-for="(error, errorIndex) in errors" 
                  :key="errorIndex"
                  class="text-sm text-red-600 ml-4"
                >
                  {{ error }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Valid Products Preview -->
        <div v-if="importedProducts.length > 0">
          <h4 class="text-sm font-medium text-slate-800 mb-2">
            Productos válidos ({{ importedProducts.length }})
          </h4>
          <div class="overflow-x-auto rounded-lg border border-slate-200 max-h-96">
            <table class="table-elegant">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>{{ configStore.getCategoryConfig(selectedCategory).hasNotes ? 'Notas' : 'Rango' }}</th>
                  <th>Factor</th>
                  <th>Laboratorio</th>
                  <th>Precio de Costo</th>
                  <th>Tiempo de Demora</th>
                  <th>Disponible</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="product in importedProducts" :key="product.Nombre">
                  <td class="font-medium">{{ product.Nombre }}</td>
                  <td>{{ product.Rango || '-' }}</td>
                  <td>{{ product.Factor }}</td>
                  <td>
                    <div v-for="lab in product.laboratories" :key="lab.laboratory.id">
                      {{ lab.laboratory.Nombre }}
                    </div>
                  </td>
                  <td>
                    <div v-for="lab in product.laboratories" :key="lab.laboratory.id">
                      ${{ lab.details.PrecioDeCosto.toLocaleString() }}
                    </div>
                  </td>
                  <td>
                    <div v-for="lab in product.laboratories" :key="lab.laboratory.id">
                      {{ lab.details.TiempoDeDemora }}
                    </div>
                  </td>
                  <td>
                    <div v-for="lab in product.laboratories" :key="lab.laboratory.id">
                      {{ lab.details.Disponible ? 'Sí' : 'No' }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="flex justify-end space-x-4">
          <button 
            @click="closeModal" 
            class="px-4 py-2 text-slate-600 hover:text-slate-800"
            :disabled="importStore.importing"
          >
            Cancelar
          </button>
          <button
            @click="importProducts"
            class="btn-primary"
            :disabled="!selectedCategory || importedProducts.length === 0 || importStore.importing"
          >
            {{ importStore.importing ? 'Importando...' : `Importar ${importedProducts.length} Productos` }}
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useCategoryStore, useLaboratoryStore } from '../stores';
import { useNotificationStore } from '../stores/notificationStore';
import { useImportStore } from '../stores/importStore';
import { useConfigStore } from '../stores/configStore';
import { processExcelData } from '../utils/importUtils';
import type { ProductWithDetails } from '../types';
import Modal from './Modal.vue';
import * as XLSX from 'xlsx';

const categoryStore = useCategoryStore();
const laboratoryStore = useLaboratoryStore();
const notificationStore = useNotificationStore();
const importStore = useImportStore();
const configStore = useConfigStore();

const showPreviewModal = ref(false);
const showGuideModal = ref(false);
const selectedCategory = ref('');
const importedProducts = ref<ProductWithDetails[]>([]);
const skippedProducts = ref<Array<{ name: string, reason: string }>>([]);
const invalidRows = ref<Record<number, string[]>>({});
const fileInput = ref<HTMLInputElement | null>(null);
const error = ref('');
const processing = ref(false);

const handleFileUpload = async (event: Event) => {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;

  if (!selectedCategory.value) {
    error.value = 'Debe seleccionar una categoría antes de importar';
    return;
  }

  processing.value = true;
  error.value = '';
  invalidRows.value = {};

  try {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array' });
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const jsonData = XLSX.utils.sheet_to_json(worksheet);
    
    const { products, skipped, invalid } = processExcelData(jsonData, selectedCategory.value);
    
    importedProducts.value = products;
    skippedProducts.value = skipped;
    invalidRows.value = invalid;

    if (products.length > 0 || skipped.length > 0) {
      showPreviewModal.value = true;
    } else {
      notificationStore.warning('No se encontraron productos válidos en el archivo');
    }

    if (Object.keys(invalid).length > 0) {
      notificationStore.warning(
        `Se encontraron ${Object.keys(invalid).length} filas con errores. ` +
        'Revise los detalles en la vista previa.'
      );
    }

    if (skipped.length > 0) {
      notificationStore.info(
        `Se saltearon ${skipped.length} productos con precio 0.`
      );
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Error al procesar el archivo';
    notificationStore.error('Error al procesar el archivo');
  } finally {
    processing.value = false;
    if (fileInput.value) {
      fileInput.value.value = '';
    }
  }
};

const closeModal = () => {
  showPreviewModal.value = false;
  showGuideModal.value = false;
  importedProducts.value = [];
  invalidRows.value = {};
  skippedProducts.value = [];
  if (fileInput.value) {
    fileInput.value.value = '';
  }
  error.value = '';
};

const downloadTemplate = () => {
  const template = [{
    Nombre: "Ejemplo Producto",
    Rango: "0 a ±2.00",
    Factor: 2.5,
    Laboratorio: laboratoryStore.laboratories[0]?.Nombre || "Laboratorio",
    PrecioDeCosto: 5000,
    TiempoDeDemora: "24hs",
    Disponible: true
  }];

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(template);
  XLSX.utils.book_append_sheet(wb, ws, "Productos");
  
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'plantilla-productos.xlsx';
  a.click();
  URL.revokeObjectURL(url);
};

const importProducts = async () => {
  if (!selectedCategory.value) {
    error.value = 'Debe seleccionar una categoría';
    return;
  }

  if (importedProducts.value.length === 0) {
    error.value = 'No hay productos para importar';
    return;
  }

  const success = await importStore.importProducts(importedProducts.value);
  if (success) {
    closeModal();
  }
};
</script>
```